<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { useRouter } from "vue-router";
  import { Lock } from "lucide-vue-next";

  import { Button } from "@/components/ui/button";

  import { useSurveyStore } from "@stores/survey/survey";
  import { useContractsStore } from "@stores/contracts";
  import { getCreditRules } from "@utils/credits/creditRules";

  const router = useRouter();
  const surveyStore = useSurveyStore();
  const contractsStore = useContractsStore();

  const { survey } = storeToRefs(surveyStore);
  const { contracts } = storeToRefs(contractsStore);

  const respondentsPerCredit = computed(
    () => getCreditRules({ aiChat: survey.value?.ai_interview }).respondentsPerCredit,
  );

  const accessibleRespondents = computed(
    () => (survey.value?.credits_consumed_auto_effective ?? 0) * respondentsPerCredit.value,
  );

  const isVisible = computed(
    () =>
      survey.value &&
      survey.value.contract_id &&
      !survey.value.credits_consumed_manual &&
      (survey.value.credits_consumed_auto ?? 0) > (survey.value.credits_consumed_auto_effective ?? 0),
  );

  const contract = computed(() =>
    contracts.value.find((c) => c.id === survey.value?.contract_id),
  );

  const isContractExhausted = computed(() => (contract.value?.credits_remaining ?? Infinity) <= 0);

  const goToSetup = () => {
    router.push(`/surveys/${survey.value.token}/setup#contract_credits`);
  };
</script>

<template>
  <div
    v-if="isVisible"
    class="rounded-lg bg-status-warning-soft border border-status-warning-border px-4 py-3 mb-4 flex items-center justify-between gap-4">
    <div class="flex items-start gap-3">
      <Lock class="h-5 w-5 text-status-warning-strong mt-0.5 flex-shrink-0" />
      <div class="text-sm text-status-warning-strong">
        <p class="font-medium">
          Results are limited to the first {{ accessibleRespondents }} respondents with your current credits
        </p>
        <p
          v-if="!isContractExhausted"
          class="text-status-warning-strong">
          Allocate more credits in the Setup page to unlock additional results.
        </p>
        <p
          v-else
          class="text-status-warning-strong">
          No credits remaining on your contract. Contact your CSM to allocate additional credits.
        </p>
      </div>
    </div>
    <Button
      v-if="!isContractExhausted"
      size="sm"
      class="bg-status-warning hover:bg-status-warning-strong text-white"
      @click="goToSetup">
      Unlock more credits
    </Button>
  </div>
</template>
